import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { navigationCategories } from "@/lib/navigation-items";
import { SynthwaveBackground } from "./SynthwaveBackground";
import { useSynthwave } from "@/lib/store/synthwave-store";
import { Footer } from "./Footer";


export function MainContent() {
  const { isSynthwaveEnabled } = useSynthwave();
  const [isGlitching, setIsGlitching] = useState(false);

  useEffect(() => {
    if (isSynthwaveEnabled) {
      document.body.classList.add("synthwave-homepage");
    } else {
      document.body.classList.remove("synthwave-homepage");
    }
    return () => {
      document.body.classList.remove("synthwave-homepage");
    };
  }, [isSynthwaveEnabled]);

  useEffect(() => {
    const getRandomInterval = () => Math.random() * 5000 + 3000;

    const triggerGlitch = () => {
      setIsGlitching(true);
      const glitchDuration = Math.random() * 100 + 100;
      setTimeout(() => {
        setIsGlitching(false);
      }, glitchDuration);
    };

    let timeoutId: NodeJS.Timeout;
    let intervalId: NodeJS.Timeout;

    timeoutId = setTimeout(() => {
      triggerGlitch();
      const scheduleNextGlitch = () => {
        intervalId = setTimeout(() => {
          triggerGlitch();
          scheduleNextGlitch();
        }, getRandomInterval());
      };
      scheduleNextGlitch();
    }, getRandomInterval());

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) {
        clearTimeout(intervalId);
      }
    };
  }, []);

  return (
    <>
      <main className="relative container mx-auto px-4 py-8 bg-transparent">
        <SynthwaveBackground />
        <div className="relative z-10 flex flex-col items-center gap-6 mb-8">
          <div className="text-4xl font-bold text-foreground">
            <div className="bg-gray-700 dark:bg-transparent px-6 py-4 rounded-2xl flex items-center justify-center">
              <div
                className={`logo-glitch-wrapper ${isGlitching ? "glitching" : ""}`}
                style={
                  { "--logo-image-url": "url('/images/branding/waryes_reloaded.png')" } as React.CSSProperties
                }
              >
                <img
                  src="/images/branding/waryes_reloaded.png"
                  alt="Logo"
                  className="h-24 w-auto max-w-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 mx-auto flex max-w-5xl flex-col gap-10">
          {navigationCategories.map((category) => (
            <section key={category.title}>
              <h2 className="mb-4 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground md:text-left">
                {category.title}
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {category.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link key={item.path} to={item.path} className="group">
                      <Card className="h-full min-h-[120px] border-border/50 bg-card/80 backdrop-blur-sm transition-all duration-200 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/10 dark:bg-card/80">
                        <CardContent className="flex h-full flex-col gap-2 p-5">
                          <div className="flex items-start gap-3">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary shadow-lg shadow-primary/10 transition-colors group-hover:bg-primary/15">
                              <Icon className="h-5 w-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="flex flex-wrap items-center gap-1.5 font-medium text-foreground transition-colors group-hover:text-primary">
                                {item.label}
                                {item.beta ? (
                                  <Badge
                                    variant="secondary"
                                    className="h-4 px-1.5 py-0 text-[10px] font-semibold"
                                  >
                                    Beta
                                  </Badge>
                                ) : null}
                              </span>
                              {item.description ? (
                                <p className="mt-1 text-xs leading-snug text-muted-foreground group-hover:text-foreground/80">
                                  {item.description}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </main>
      <Footer />
    </>
  );
}
